import React from 'react';
import { AlertTriangle, Calendar, Clock, CheckCircle } from 'lucide-react';
import { QuickStatsCard } from '@features/cx/shared/components/QuickStatsCard';
import { useConsumerSummary } from '../../hooks';
import { getRemoteUtilityId } from '@shared/utils/getUtilityId';

export const ConsumerSummary: React.FC = () => {
  const remoteUtilityId = getRemoteUtilityId();
  
  const { data: summaryData, isLoading, error } = useConsumerSummary({
    remote_utility_id: remoteUtilityId,
  });

  // Show loading state or handle error
  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-white rounded-lg p-6 shadow-sm animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-2"></div>
            <div className="h-3 bg-gray-200 rounded w-full"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-600">Failed to load consumer summary data. Please try again.</p>
      </div>
    );
  }

  // Ensure summaryData exists before accessing its properties
  const summary = summaryData || {
    totalConsumers: 0,
    activeConsumers: 0,
    inactiveConsumers: 0,
    temporarilyDisconnected: 0,
    activePercentage: 0,
    inactivePercentage: 0,
    temporarilyDisconnectedPercentage: 0,
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      <QuickStatsCard 
        title="Total Accounts"
        count={summary.totalConsumers || 0}
        trendSummary={'All consumer accounts in system'}
        trendDirection={summary.totalConsumers >= 0 ? "up" : "down"}
        icon={<AlertTriangle className="h-8 w-8 text-amber-500" />}
      />
      <QuickStatsCard 
        title="Active Accounts"
        count={summary.activeConsumers || 0}
        trendSummary={`${summary.activePercentage || 0}% of Total Accounts`}
        trendDirection={summary.activeConsumers >= 0 ? "up" : "down"}
        icon={<Calendar className="h-8 w-8 text-blue-500" />}
      />
      <QuickStatsCard 
        title="Inactive Accounts"
        count={summary.inactiveConsumers || 0}
        trendSummary={`${summary.inactivePercentage || 0}% of Total Accounts`}
        trendDirection={summary.inactiveConsumers >= 0 ? "up" : "down"}
        icon={<Clock className="h-8 w-8 text-purple-500" />}
      />
      <QuickStatsCard 
        title="Temporarily Disconnected"
        count={summary.temporarilyDisconnected || 0}
        trendSummary={`${summary.temporarilyDisconnectedPercentage || 0}% of Total Accounts`}
        trendDirection={summary.temporarilyDisconnected >= 0 ? "up" : "down"}
        icon={<CheckCircle className="h-8 w-8 text-green-500" />}
      />
    </div>
  );
};
